import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { MapPin, Calendar, Package, Info, ArrowLeft, Gavel, Clock, BadgeCheck, Trophy, CheckCircle2, XCircle, Truck, Route, MailWarning, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { RatingStars } from "@/components/RatingStars";
import { Star, Phone, Mail, ShieldCheck, Sparkles, Video } from "lucide-react";
import { InventoryReview } from "@/components/VideoInventory";
import { AccessView, MovePlanCard } from "@/components/MovePlan";
import { BidExtrasView } from "@/components/BidExtras";
import { BidDialog } from "@/pages/RemovalistDashboard";
import { fmtExpiry } from "@/components/DatePickerAU";
import { AcceptVerifyCard } from "@/components/AcceptVerifyCard";
import { fmtDuration, fmtDate, addDays } from "@/lib/format";
import { toast } from "sonner";

function AcceptDialog({ bid, onAccept }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [method, setMethod] = useState("email");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [time, setTime] = useState("");
  const opts = bid.date_options || [];
  const cheapest = opts.length ? opts.reduce((a, o) => (o.amount < a.amount ? o : a), opts[0]) : null;
  const [day, setDay] = useState(cheapest?.date || "");
  const chosen = opts.find((o) => o.date === day);
  const submit = () => {
    onAccept(bid.id, { name, method, email, phone, contact_time: method === "phone" ? time : "" }, opts.length > 1 ? day : undefined);
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (o) setDay(cheapest?.date || ""); setOpen(o); }}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid={`accept-bid-btn-${bid.id}`} className="flex-1 bg-asphalt hover:bg-asphalt-hover text-white gap-2"><CheckCircle2 className="h-4 w-4" /> Accept</Button>
      </DialogTrigger>
      <DialogContent className="bg-card max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Confirm & share your contact details</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Accepting {bid.removalist_name}'s ${chosen ? chosen.amount : bid.amount} bid{chosen ? ` for ${fmtDate(chosen.date)}` : ""}. Tell them how to reach you to arrange the move.</p>
        <div className="space-y-4 py-2">
          {opts.length > 1 && (
            <div className="space-y-2" data-testid="accept-day-options">
              <Label>Choose your move day</Label>
              <div className="grid grid-cols-2 gap-2">
                {opts.map((o) => (
                  <button key={o.date} type="button" onClick={() => setDay(o.date)} data-testid={`accept-day-${o.date}`} aria-pressed={day === o.date}
                    className={`rounded-lg border p-2.5 text-left transition-colors ${day === o.date ? "border-kinetic bg-kinetic-light" : "border-border hover:border-kinetic/50"}`}>
                    <span className="block text-xs text-muted-foreground">{fmtDate(o.date)}</span>
                    <span className="font-bold">${o.amount}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-2"><Label>Your name</Label><Input data-testid="contact-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" /></div>
          <div className="space-y-2">
            <Label>Preferred contact method</Label>
            <div className="flex gap-2">
              <Button type="button" onClick={() => setMethod("email")} data-testid="contact-method-email" variant={method === "email" ? "default" : "outline"} className={`flex-1 gap-2 ${method === "email" ? "bg-kinetic hover:bg-kinetic-hover text-white" : ""}`}><Mail className="h-4 w-4" /> Email</Button>
              <Button type="button" onClick={() => setMethod("phone")} data-testid="contact-method-phone" variant={method === "phone" ? "default" : "outline"} className={`flex-1 gap-2 ${method === "phone" ? "bg-kinetic hover:bg-kinetic-hover text-white" : ""}`}><Phone className="h-4 w-4" /> Phone</Button>
            </div>
          </div>
          <div className="space-y-2"><Label>Email</Label><Input data-testid="contact-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" /></div>
          {method === "phone" && (
            <>
              <div className="space-y-2"><Label>Phone number</Label><Input data-testid="contact-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+61 400 000 000" /></div>
              <div className="space-y-2"><Label>Best time / date to call</Label><Input data-testid="contact-time-input" value={time} onChange={(e) => setTime(e.target.value)} placeholder="Weekdays after 5pm" /></div>
            </>
          )}
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="accept-verify-note"><ShieldCheck className="h-3.5 w-3.5 text-kinetic shrink-0" /> First time using these details? We'll email you a 6-digit code to confirm before the removalist is notified.</p>
        <DialogFooter><Button data-testid="confirm-accept-btn" onClick={submit} className="bg-asphalt hover:bg-asphalt-hover text-white gap-2"><CheckCircle2 className="h-4 w-4" /> Accept bid</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RateDialog({ onSubmit }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="rate-removalist-btn" className="gap-2 bg-amber-500 hover:bg-amber-600 text-white"><Star className="h-4 w-4" /> Rate removalist</Button>
      </DialogTrigger>
      <DialogContent className="bg-card">
        <DialogHeader><DialogTitle className="font-display">Rate your removalist</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex flex-col items-center gap-2">
            <RatingStars value={rating} size={32} editable onChange={setRating} />
            <span className="text-sm text-muted-foreground">{rating} / 5</span>
          </div>
          <Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="How was the move? (optional)" />
        </div>
        <DialogFooter>
          <Button data-testid="submit-review-btn" onClick={() => { onSubmit(rating, comment); setOpen(false); }} className="bg-kinetic hover:bg-kinetic-hover text-white">Submit review</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const justPosted = params.get("posted") === "1";
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [review, setReview] = useState(null);
  const [bidSort, setBidSort] = useState("price");

  const load = () => {
    api.get(`/jobs/${id}`).then(({ data }) => setJob(data)).finally(() => setLoading(false));
    api.get(`/jobs/${id}/review`).then(({ data }) => setReview(data && data.id ? data : null)).catch(() => {});
  };
  useEffect(() => { load(); }, [id]);

  if (loading) return <div className="min-h-screen bg-background"><Navbar /><p className="text-center py-20 text-muted-foreground">Loading job…</p></div>;
  if (!job) return <div className="min-h-screen bg-background"><Navbar /><p className="text-center py-20 text-muted-foreground">Job not found.</p></div>;

  const isOwner = user?.role === "customer" && job.customer_id === user.id;
  const wonByMe = user?.role === "removalist" && job.bids?.some((b) => b.id === job.accepted_bid_id && b.removalist_id === user.id);

  const accept = async (bidId, card, selectedDate) => {
    try {
      const { data } = await api.post(`/jobs/${id}/accept-bid/${bidId}`, { contact_card: card, selected_date: selectedDate });
      if (data.verification_required) toast.success(`We've emailed a code to ${data.pending_acceptance?.email}. Enter it to send your acceptance.`);
      else toast.success("Bid accepted! The removalist has been notified by email.");
      load();
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const withdraw = async (bidId) => {
    try { await api.post(`/bids/${bidId}/withdraw`); toast.success("Bid withdrawn"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const setStatus = async (status) => {
    try { await api.patch(`/jobs/${id}/status`, { status }); toast.success(`Job marked ${status}`); if (status === "open") setParams({ posted: "1" }, { replace: true }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const submitReview = async (rating, comment) => {
    try { const { data } = await api.post(`/jobs/${id}/review`, { rating, comment }); setReview(data); toast.success("Thanks for your review!"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const visibleBids = job.bids.filter((b) => b.status !== "withdrawn");
  const lowestAmount = visibleBids.some((b) => !b.is_expired) ? Math.min(...visibleBids.filter((b) => !b.is_expired).map((b) => b.amount)) : null;
  const sortedBids = [...visibleBids].sort((a, b) => {
    if (bidSort === "date") {
      const ad = a.desired_date || "9999-12-31";
      const bd = b.desired_date || "9999-12-31";
      if (ad !== bd) return ad < bd ? -1 : 1;
    }
    return a.amount - b.amount;
  });

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="h-4 w-4" /> Back to dashboard</button>

        {justPosted && isOwner && (
          <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 p-4 mb-6" data-testid="posted-spam-note">
            <MailWarning className="h-5 w-5 shrink-0 mt-0.5 text-amber-600" />
            <p className="text-sm flex-1"><strong>Your move is live — bid alerts are on their way by email.</strong> Check your Spam or Junk folder for emails from MoveTender and mark them Not spam so you don't miss a quote.</p>
            <button type="button" onClick={() => setParams({}, { replace: true })} aria-label="Dismiss" className="text-amber-700 hover:text-amber-900" data-testid="posted-spam-note-dismiss"><X className="h-4 w-4" /></button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Job spec */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="font-display text-3xl font-bold">{job.title}</h1>
                <p className="text-sm text-muted-foreground mt-1">Posted by {job.customer_name}</p>
              </div>
              <StatusBadge status={job.status} />
            </div>

            <Card className="p-6 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <p className="font-mono text-xs text-muted-foreground uppercase mb-1 flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-emerald-500" /> Pickup</p>
                  <p className="font-medium">{job.pickup_address}</p>
                </div>
                <div>
                  <p className="font-mono text-xs text-muted-foreground uppercase mb-1 flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-kinetic" /> Drop-off</p>
                  <p className="font-medium">{job.dropoff_address}</p>
                </div>
                {job.preferred_date && (
                  <div>
                    <p className="font-mono text-xs text-muted-foreground uppercase mb-1 flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {(job.flex_days_before || job.flex_days_after) ? "Preferred date (flexible)" : "Preferred date"}</p>
                    <p className="font-medium">{fmtDate(job.preferred_date)}</p>
                    {(job.flex_days_before || job.flex_days_after) ? (
                      <p className="text-xs text-muted-foreground" data-testid="job-flex-window">Flexible: {fmtDate(addDays(job.preferred_date, -(job.flex_days_before || 0)))} – {fmtDate(addDays(job.preferred_date, job.flex_days_after || 0))}</p>
                    ) : null}
                  </div>
                )}
                {job.distance_km != null && (
                  <div>
                    <p className="font-mono text-xs text-muted-foreground uppercase mb-1 flex items-center gap-1"><Route className="h-3.5 w-3.5 text-asphalt" /> {job.distance_is_driving ? "Driving distance" : "Distance"}</p>
                    <p className="font-medium">{job.distance_km} km{job.duration_min != null ? ` · ${fmtDuration(job.duration_min)} drive` : ""}</p>
                  </div>
                )}
              </div>
              {job.inventory?.rooms?.length > 0 && (
                <div data-testid="job-ai-inventory">
                  <p className="font-mono text-xs text-muted-foreground uppercase mb-2 flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-kinetic" /> <span data-testid="job-inventory-heading">{(job.inventory.videos?.length || job.inventory.video_path) ? "AI video inventory" : "Itemised inventory"}</span></p>
                  <InventoryReview rooms={job.inventory.rooms} onChange={() => {}} readOnly unload={job.move_plan?.unload} />
                </div>
              )}
              {(job.inventory?.videos?.length > 0 || job.inventory?.video_path) && (
                <div data-testid="job-walkthrough-video">
                  <p className="font-mono text-xs text-muted-foreground uppercase mb-2 flex items-center gap-1"><Video className="h-3.5 w-3.5" /> Room videos</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {(job.inventory.videos?.length ? job.inventory.videos : [{ path: job.inventory.video_path, room_name: "Walkthrough" }]).map((v, i) => (
                      <div key={v.path} data-testid={`job-room-video-${i}`}>
                        <p className="text-sm font-medium mb-1">{v.room_name || `Video ${i + 1}`}</p>
                        <video controls preload="metadata" src={`${API}/files/${v.path}`} className="w-full max-h-64 rounded-lg border border-border bg-black" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {job.inventory_description && (
                <div>
                  <p className="font-mono text-xs text-muted-foreground uppercase mb-1 flex items-center gap-1"><Package className="h-3.5 w-3.5" /> Inventory</p>
                  <p className="text-sm leading-relaxed">{job.inventory_description}</p>
                </div>
              )}
              <AccessView access={job.access} legacyNotes={job.access_notes} />
              {job.photos?.length > 0 && (
                <div>
                  <p className="font-mono text-xs text-muted-foreground uppercase mb-2">Photos</p>
                  <div className="flex flex-wrap gap-3">
                    {job.photos.map((p, i) => (
                      <a key={i} href={`${API}/files/${p}`} target="_blank" rel="noreferrer" className="h-28 w-28 rounded-lg overflow-hidden border border-border block">
                        <img src={`${API}/files/${p}`} alt="" className="h-full w-full object-cover hover:scale-105 transition-transform" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {job.can_download_inventory && (
              <MovePlanCard key={JSON.stringify([job.move_plan, job.access, job.inventory?.rooms])} job={job}
                editable={isOwner && !(["completed", "cancelled"].includes(job.status))} onSaved={load} />
            )}

            {isOwner && (
              <div className="flex flex-wrap gap-3">
                {job.status === "accepted" && (
                  <Button onClick={() => setStatus("completed")} data-testid="mark-completed-btn" className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="h-4 w-4" /> Mark completed</Button>
                )}
                {["open", "draft"].includes(job.status) && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" data-testid="cancel-job-btn" className="gap-2 text-destructive border-destructive/30"><XCircle className="h-4 w-4" /> Cancel job</Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-card">
                      <AlertDialogHeader><AlertDialogTitle>Cancel this job?</AlertDialogTitle><AlertDialogDescription>This will close the auction. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Keep job</AlertDialogCancel><AlertDialogAction onClick={() => setStatus("cancelled")} className="bg-destructive">Cancel job</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                {job.status === "draft" && (
                  <Button onClick={() => setStatus("open")} data-testid="publish-job-btn" className="bg-kinetic hover:bg-kinetic-hover text-white gap-2"><Gavel className="h-4 w-4" /> Publish for bids</Button>
                )}
              </div>
            )}

            {isOwner && job.status === "completed" && (
              <Card className="p-5">
                <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><Star className="h-4 w-4 text-amber-400" /> Your review</h3>
                {review ? (
                  <div data-testid="job-review">
                    <RatingStars value={review.rating} size={18} />
                    {review.comment && <p className="text-sm text-muted-foreground mt-2">{review.comment}</p>}
                  </div>
                ) : (
                  <RateDialog onSubmit={submitReview} />
                )}
              </Card>
            )}

            {!isOwner && user?.role === "removalist" && review && (
              <Card className="p-5">
                <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><Star className="h-4 w-4 text-amber-400" /> Customer review</h3>
                <div data-testid="job-review">
                  <RatingStars value={review.rating} size={18} />
                  {review.comment && <p className="text-sm text-muted-foreground mt-2">{review.comment}</p>}
                </div>
              </Card>
            )}

            {user?.role === "removalist" && wonByMe && job.contact_card && (
              <Card className="p-5 border-emerald-200 bg-emerald-50/40" data-testid="customer-contact-card">
                <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><Phone className="h-4 w-4 text-emerald-600" /> Customer contact</h3>
                <div className="space-y-1.5 text-sm">
                  {job.contact_card.name && <p><span className="text-muted-foreground">Name:</span> <span className="font-medium">{job.contact_card.name}</span></p>}
                  <p><span className="text-muted-foreground">Preferred method:</span> <span className="font-medium capitalize">{job.contact_card.method}</span></p>
                  {job.contact_card.email && <p className="flex items-center gap-1"><Mail className="h-3.5 w-3.5 text-muted-foreground" /> <a href={`mailto:${job.contact_card.email}`} className="font-medium text-kinetic">{job.contact_card.email}</a></p>}
                  {job.contact_card.phone && <p className="flex items-center gap-1"><Phone className="h-3.5 w-3.5 text-muted-foreground" /> <a href={`tel:${job.contact_card.phone}`} className="font-medium text-kinetic">{job.contact_card.phone}</a></p>}
                  {job.contact_card.contact_time && <p><span className="text-muted-foreground">Best time:</span> <span className="font-medium">{job.contact_card.contact_time}</span></p>}
                </div>
              </Card>
            )}

          </div>

          {/* Bids */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl font-bold flex items-center gap-2"><Gavel className="h-5 w-5 text-kinetic" /> Bids</h2>
                <Badge variant="outline" className="font-mono">{visibleBids.length}</Badge>
              </div>
              {user?.role === "removalist" && job.status === "open" && (() => {
                const mine = job.bids?.find((b) => b.removalist_id === user.id && b.status !== "withdrawn");
                return (
                  <Card className="p-4 mb-4 border-kinetic/30 bg-kinetic-light/50" data-testid="job-detail-bid-card">
                    <p className="text-sm font-medium mb-1">{mine ? "You've bid on this job" : "Interested in this move?"}</p>
                    <p className="text-xs text-muted-foreground mb-3">{mine ? "Update your price, days or inclusions any time while the job is open." : "Review the inventory and access details, then send the customer your offer."}</p>
                    <BidDialog job={{ ...job, my_bid: mine }} myBid={mine} onDone={load} triggerClassName="w-full h-10" />
                  </Card>
                );
              })()}
              {isOwner && job.pending_acceptance && (
                <AcceptVerifyCard jobId={id} pending={job.pending_acceptance} onDone={load}
                  removalistName={job.bids?.find((b) => b.id === job.pending_acceptance.bid_id)?.removalist_name} />
              )}
              {visibleBids.length > 1 && (
                <div className="flex items-center gap-2 mb-4 text-xs" data-testid="bid-sort-toggle">
                  <span className="text-muted-foreground">Sort by</span>
                  <div className="inline-flex rounded-lg border border-border p-0.5">
                    <button onClick={() => setBidSort("price")} data-testid="sort-by-price-btn" className={`px-2.5 py-1 rounded-md font-medium transition-colors ${bidSort === "price" ? "bg-asphalt text-white" : "text-muted-foreground"}`}>Price</button>
                    <button onClick={() => setBidSort("date")} data-testid="sort-by-date-btn" className={`px-2.5 py-1 rounded-md font-medium flex items-center gap-1 transition-colors ${bidSort === "date" ? "bg-asphalt text-white" : "text-muted-foreground"}`}><Calendar className="h-3 w-3" /> Move date</button>
                  </div>
                </div>
              )}

              <div className="space-y-3" data-testid="bids-list">
                {visibleBids.length === 0 && (
                  <Card className="p-6 text-center text-sm text-muted-foreground">
                    <Truck className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    No bids yet. Removalists will place offers soon.
                  </Card>
                )}
                {sortedBids.map((b, idx) => {
                  const isWinner = b.status === "accepted";
                  const isLowest = !b.is_expired && b.amount === lowestAmount && job.status === "open";
                  return (
                    <Card key={b.id} data-testid={`bid-item-${b.id}`}
                      className={`p-4 ${isWinner ? "border-emerald-300 bg-emerald-50/50" : isLowest ? "border-kinetic/40" : ""}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          {b.removalist_has_logo ? <img src={`${API}/public/logo/${b.removalist_id}`} alt="" className="h-9 w-9 rounded-full border border-border object-contain bg-white" data-testid={`bid-logo-${b.id}`} /> : <Avatar className="h-9 w-9"><AvatarFallback className="bg-asphalt text-white text-xs">{b.removalist_name?.slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>}
                          <div>
                            <Link to={`/removalists/${b.removalist_id}`} data-testid={`removalist-link-${b.removalist_id}`} className="font-semibold text-sm leading-tight flex items-center gap-1 hover:text-kinetic">{b.removalist_name}{b.removalist_verified && <BadgeCheck className="h-3.5 w-3.5 text-emerald-500" />}</Link>
                            {b.removalist_zones?.length > 0 && <p className="text-xs text-muted-foreground truncate max-w-[140px]">{b.removalist_zones.join(", ")}</p>}
                            {b.removalist_insurance && <p className="text-xs text-emerald-700 flex items-center gap-1 truncate max-w-[160px]" data-testid={`bid-insurance-${b.id}`}><ShieldCheck className="h-3 w-3 shrink-0" /> Insured</p>}
                            <div className="mt-0.5"><RatingStars value={b.removalist_rating || 0} count={b.removalist_rating_count ?? 0} size={12} /></div>
                          </div>
                        </div>
                        {isWinner && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1"><Trophy className="h-3 w-3" /> Winner</Badge>}
                        {b.is_expired && <Badge variant="outline" className="text-red-600 border-red-300" data-testid={`bid-expired-${b.id}`}>Expired</Badge>}
                        {isLowest && !isWinner && <Badge className="bg-kinetic-light text-kinetic border-kinetic/30">Lowest</Badge>}
                        {isOwner && job.pending_acceptance?.bid_id === b.id && <Badge variant="outline" className="text-amber-700 border-amber-300 gap-1" data-testid={`bid-awaiting-verify-${b.id}`}><ShieldCheck className="h-3 w-3" /> Awaiting code</Badge>}
                      </div>
                      {(() => {
                        const multi = b.date_options?.length > 1 && b.status === "pending";
                        const varied = multi && new Set(b.date_options.map((o) => o.amount)).size > 1;
                        return (
                          <>
                            <div className="flex items-center gap-4 mb-2">
                              <span className="font-display text-2xl font-bold" data-testid={`bid-price-${b.id}`}>{varied && <span className="text-sm font-medium text-muted-foreground mr-1">from</span>}${b.amount}</span>
                              <span className="flex items-center gap-1 text-sm text-muted-foreground"><Clock className="h-4 w-4" /> {b.estimated_hours}h</span>
                            </div>
                            {multi ? (
                              <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`bid-day-options-${b.id}`}>
                                {b.date_options.map((o) => (
                                  <span key={o.date} className={`text-xs rounded-md border px-2 py-1 ${o.amount === b.amount ? "border-kinetic bg-kinetic-light" : "border-border"}`}>
                                    <Calendar className="inline h-3 w-3 mr-1 text-kinetic" />{fmtDate(o.date)} · <b>${o.amount}</b>
                                  </span>
                                ))}
                              </div>
                            ) : b.desired_date && <p className="flex items-center gap-1.5 text-sm mb-2" data-testid={`bid-desired-date-${b.id}`}><Calendar className="h-4 w-4 text-kinetic" /> Move date: <span className="font-medium">{fmtDate(b.desired_date)}</span></p>}
                          </>
                        );
                      })()}
                      <BidExtrasView bid={b} />
                      {b.expires_at && b.status === "pending" && (
                        <p className={`flex items-center gap-1.5 text-xs mb-2 ${b.is_expired ? "text-red-600" : "text-amber-700"}`} data-testid={`bid-expiry-${b.id}`}>
                          <Clock className="h-3.5 w-3.5" /> {b.is_expired ? "Expired" : "Valid until"} {fmtExpiry(b.expires_at)}
                        </p>
                      )}
                      {b.notes && <p className="text-sm text-muted-foreground mb-3">{b.notes}</p>}
                      {isOwner && job.status === "open" && !b.is_expired && job.pending_acceptance?.bid_id !== b.id && (
                        <div className="flex gap-2"><AcceptDialog bid={b} onAccept={accept} /></div>
                      )}
                      {user?.role === "removalist" && b.removalist_id === user.id && b.status === "pending" && job.status === "open" && (
                        <Button size="sm" variant="outline" onClick={() => withdraw(b.id)} data-testid={`withdraw-bid-btn-${b.id}`} className="w-full gap-1 text-destructive border-destructive/30 mt-1"><XCircle className="h-4 w-4" /> Withdraw bid</Button>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
